import React, { useState, useEffect } from 'react';
import FamilyInput from './FamilyInput';
import RIBAttestationModal from './RIBAttestationModal';
import { Copy, User, CreditCard, Users, Hash, Loader2, CheckCircle, FileText } from 'lucide-react';

const TestDataAPIInterface = () => {
  const [ibanData, setIbanData] = useState(null);
  const [ssnData, setSsnData] = useState(null);
  const [familyData, setFamilyData] = useState(null);
  const [customFamily, setCustomFamily] = useState('');
  const [loading, setLoading] = useState({});
  const [copiedField, setCopiedField] = useState('');
  const [isRibModalOpen, setIsRibModalOpen] = useState(false);

  let API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3000';
  if (API_BASE_URL == "/")
    API_BASE_URL = window.location.origin;



  const formatDateFR = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR');
  };

  const handleCopy = async (text, fieldName) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(fieldName);
      setTimeout(() => setCopiedField(''), 2000);
    } catch (err) {
      console.error('Erreur lors de la copie:', err);
    }
  };

  const fetchIban = async () => {
    setLoading(prev => ({ ...prev, iban: true }));
    try {
      const response = await fetch(`${API_BASE_URL}/api/iban`);
      const data = await response.json();
      setIbanData(data);
    } catch (error) {
      console.error('Erreur lors de la récupération IBAN:', error);
    } finally {
      setLoading(prev => ({ ...prev, iban: false }));
    }
  };

  const fetchSSN = async () => {
    setLoading(prev => ({ ...prev, ssn: true }));
    try {
      const response = await fetch(`${API_BASE_URL}/api/ssn`);
      const data = await response.json();
      setSsnData(data);
    } catch (error) {
      console.error('Erreur lors de la récupération SSN:', error);
    } finally {
      setLoading(prev => ({ ...prev, ssn: false }));
    }
  };

  const fetchFamily = async (customAges = null) => {
    setLoading(prev => ({ ...prev, family: true }));
    try {
      const url = `${API_BASE_URL}/api/family`;
      const options = {
        method: customAges ? 'POST' : 'GET',
        headers: customAges ? { 'Content-Type': 'application/json' } : {},
        body: customAges ? JSON.stringify({ family: customAges }) : undefined
      };

      const response = await fetch(url, options);
      const data = await response.json();
      setFamilyData(data);
    } catch (error) {
      console.error('Erreur lors de la récupération famille:', error);
    } finally {
      setLoading(prev => ({ ...prev, family: false }));
    }
  };

  // Chargement initial des données
  useEffect(() => {
    fetchIban();
    fetchSSN();
    fetchFamily();
  }, []); // Les fonctions sont maintenant définies avant le useEffect

  const handleCustomFamilySubmit = () => {
    const ages = customFamily
      .split(',')
      .map(age => parseInt(age.trim()))
      .filter(age => !isNaN(age) && age > 0);

    if (ages.length > 0) {
      fetchFamily(ages);
    }
  };

  const DataCard = ({ title, children, icon: Icon }) => (
    <div className="bg-white rounded-lg shadow-md border border-gray-200 p-6">
      <div className="flex items-center mb-4">
        <Icon className="w-6 h-6 text-blue-600 mr-2" />
        <h2 className="text-xl font-semibold text-gray-800">{title}</h2>
      </div>
      {children}
    </div>
  );

  const CopyButton = ({ text, fieldName }) => (
    <button
      onClick={() => handleCopy(text, fieldName)}
      className="ml-2 p-1 text-gray-400 hover:text-gray-600 transition-colors"
      title="Copier"
    >
      {copiedField === fieldName ? (
        <CheckCircle className="w-4 h-4 text-green-500" />
      ) : (
        <Copy className="w-4 h-4" />
      )}
    </button>
  );

  const LoadingButton = ({ onClick, loading, children, variant = 'primary' }) => {
    const baseClasses = "px-4 py-2 rounded-md font-medium transition-colors flex items-center justify-center";
    const variants = {
      primary: "bg-blue-600 text-white hover:bg-blue-700 disabled:bg-blue-400",
      secondary: "bg-gray-600 text-white hover:bg-gray-700 disabled:bg-gray-400"
    };

    return (
      <button
        onClick={onClick}
        disabled={loading}
        className={`${baseClasses} ${variants[variant]}`}
      >
        {loading ? (
          <>
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            Chargement...
          </>
        ) : (
          children
        )}
      </button>
    );
  };

  const RelationBadge = ({ type }) => {
    const colors = {
      conjoint: 'bg-pink-100 text-pink-800',
      child: 'bg-blue-100 text-blue-800'
    };

    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${colors[type] || 'bg-gray-100 text-gray-800'}`}>
        {type}
      </span>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            API de Génération de Données de Test
          </h1>
          <p className="text-gray-600">
            Récupérer des données aléatoires pour vos tests et vos formations.
          </p>

        </div>

        <div className="mb-8">
          {/* Section Famille */}
          <DataCard title="Famille" icon={Users}>
            <div className="space-y-4">
              <div className="flex flex-wrap gap-3">
                <LoadingButton
                  onClick={() => fetchFamily()}
                  loading={loading.family}
                >
                  Générer Famille Aléatoire
                </LoadingButton>

                <FamilyInput
                  customFamily={customFamily}
                  setCustomFamily={setCustomFamily}
                  handleCustomFamilySubmit={handleCustomFamilySubmit}
                  loading={loading.family}
                />
              </div>

              {familyData && familyData.family && (
                <div className="mt-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                    {familyData.family.map((member, index) => (
                      <div key={member.ref} className="bg-gray-50 rounded-lg p-4 border">
                        <div className="flex items-center mb-3">
                          <User className="w-5 h-5 text-gray-600 mr-2" />
                          <div>
                            <h3 className="font-semibold text-gray-900">
                              {member.first_name} {member.name}
                            </h3>
                            <p className="text-sm text-gray-600">
                              {member.gender === 'male' ? '♂' : '♀'} • {formatDateFR(member.birth_date)}
                            </p>
                          </div>
                        </div>

                        <div className="space-y-2 text-sm">
                          {/* Prénom avec copie */}
                          <div className="flex justify-between items-center">
                            <span className="text-gray-600">Prénom:</span>
                            <div className="flex items-center">
                              <span>{member.first_name}</span>
                              <CopyButton text={member.first_name} fieldName={`first-name-${member.ref}`} />
                            </div>
                          </div>

                          {/* Nom avec copie */}
                          <div className="flex justify-between items-center">
                            <span className="text-gray-600">Nom:</span>
                            <div className="flex items-center">
                              <span>{member.name}</span>
                              <CopyButton text={member.name} fieldName={`last-name-${member.ref}`} />
                            </div>
                          </div>

                          {/* Date de naissance avec copie */}
                          <div className="flex justify-between items-center">
                            <span className="text-gray-600">Date naissance:</span>
                            <div className="flex items-center">
                              <span>{formatDateFR(member.birth_date)}</span>
                              <CopyButton text={formatDateFR(member.birth_date)} fieldName={`birth-${member.ref}`} />
                            </div>
                          </div>

                          {member.ssn && (
                            <div className="flex justify-between items-center">
                              <span className="text-gray-600">SSN:</span>
                              <div className="flex items-center">
                                <span className="font-mono">{member.ssn}</span>
                                <CopyButton text={member.ssn} fieldName={`ssn-${member.ref}`} />
                              </div>
                            </div>
                          )}

                          {member.phone && (
                            <div className="flex justify-between items-center">
                              <span className="text-gray-600">Tel:</span>
                              <div className="flex items-center">
                                <span className="font-mono">{member.phone}</span>
                                <CopyButton text={member.phone} fieldName={`phone-${member.ref}`} />
                              </div>
                            </div>
                          )}

                          {member.email && (
                            <div className="flex justify-between items-center">
                              <span className="text-gray-600">Email:</span>
                              <div className="flex items-center">
                                <span className="font-mono text-xs">{member.email}</span>
                                <CopyButton text={member.email} fieldName={`email-${member.ref}`} />
                              </div>
                            </div>
                          )}

                          {member.addresses && member.addresses.length > 0 && (
                            <>
                              {/* Adresse avec copie */}
                              <div className="flex justify-between items-center">
                                <span className="text-gray-600">Adresse:</span>
                                <div className="flex items-center">
                                  <span className="text-xs">{member.addresses[0].street}</span>
                                  <CopyButton text={member.addresses[0].street} fieldName={`address-${member.ref}`} />
                                </div>
                              </div>

                              {/* Code postal avec copie */}
                              <div className="flex justify-between items-center">
                                <span className="text-gray-600">Code postal:</span>
                                <div className="flex items-center">
                                  <span>{member.addresses[0].zip}</span>
                                  <CopyButton text={member.addresses[0].zip} fieldName={`zip-${member.ref}`} />
                                </div>
                              </div>

                              {/* Ville avec copie */}
                              <div className="flex justify-between items-center">
                                <span className="text-gray-600">Ville:</span>
                                <div className="flex items-center">
                                  <span>{member.addresses[0].city}</span>
                                  <CopyButton text={member.addresses[0].city} fieldName={`city-${member.ref}`} />
                                </div>
                              </div>
                            </>
                          )}

                          {member.relations && member.relations.length > 0 && (
                            <div className="flex flex-wrap gap-1 mt-2">
                              {member.relations.map((relation, idx) => (
                                <RelationBadge key={idx} type={relation.type} />
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </DataCard>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Section SSN */}
          <DataCard title="Numéro de Sécurité Sociale" icon={Hash}>
            <LoadingButton
              onClick={fetchSSN}
              loading={loading.ssn}
            >
              Générer SSN
            </LoadingButton>

            {ssnData && (
              <div className="mt-4">
                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-md">
                  <div>
                    <span className="text-sm text-gray-600">SSN:</span>
                    <p className="font-mono text-sm">{ssnData.ssn}</p>
                  </div>
                  <CopyButton text={ssnData.ssn} fieldName="ssn" />
                </div>
              </div>
            )}
          </DataCard>
          {/* Section IBAN */}
          <DataCard title="IBAN & BIC" icon={CreditCard}>
            <LoadingButton
              onClick={fetchIban}
              loading={loading.iban}
            >
              Générer IBAN
            </LoadingButton>

            {ibanData && (
              <div className="mt-4 space-y-2">
                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-md">
                  <div>
                    <span className="text-sm text-gray-600">IBAN:</span>
                    <p className="font-mono text-sm">{ibanData.iban}</p>
                  </div>
                  <CopyButton text={ibanData.iban} fieldName="iban" />
                </div>
                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-md">
                  <div>
                    <span className="text-sm text-gray-600">BIC:</span>
                    <p className="font-mono text-sm">{ibanData.bic}</p>
                  </div>
                  <CopyButton text={ibanData.bic} fieldName="bic" />
                </div>
                <button
                  onClick={() => setIsRibModalOpen(true)}
                  className="w-full mt-2 px-4 py-2 bg-green-600 text-white hover:bg-green-700 rounded-md font-medium transition-colors flex items-center justify-center"
                >
                  <FileText className="w-4 h-4 mr-2" />
                  Générer Attestation RIB
                </button>
              </div>
            )}
          </DataCard>
        </div>

        {/* RIB Attestation Modal */}
        <RIBAttestationModal
          isOpen={isRibModalOpen}
          onClose={() => setIsRibModalOpen(false)}
          defaultFirstName={familyData?.family?.[0]?.first_name || ''}
          defaultLastName={familyData?.family?.[0]?.name || ''}
          defaultIban={ibanData?.iban || ''}
          defaultBic={ibanData?.bic || ''}
        />
      </div>
    </div>
  );
};

export default TestDataAPIInterface;